/**
 * Org Y.Doc Store
 *
 * WS-only connection (no IndexedDB — the org room is re-seeded from MongoDB
 * on every cold start, which is incompatible with safe IDB caching).
 *
 * Room name: org:{dataspaceId}
 *
 * Y.Doc structure:
 *   Y.Map('edges') — edgeId → JSON { parentId, childId, pos, expanded, kind }
 *   Y.Map('nodes') — entityId → JSON { name, icon, entityType }
 *
 * Observers push changes into dataspaceTreeStore and the entity registry
 * (spaceEntityStore + globalEntityStore) so the sidebar updates in real time
 * when the backend (or another client) mutates tree structure or renames
 * entities.
 *
 * ⚠️  Snapshot ceiling: The websocket server enforces PAYLOAD_BUDGET_BYTES['org:']
 *     (default 16 MiB) for the initial full-room snapshot. Normal orgs are well
 *     under this limit. If a very large org triggers snapshot truncation the
 *     global tree will be partially visible on cold start (subsequent CRDT ops
 *     will fill it in). A follow-up can add lazy subtree edge sync using the
 *     REST GET /api/dataspaces/{id}/edges?parent_id=<id> filter to stream
 *     deeper subtrees on demand rather than front-loading them.
 */

import * as Y from 'yjs'
import type { WebsocketProvider } from 'y-websocket'
import fetchService from '../services/fetchService'
import { MAIL_CRDT_TYPES } from './mailCrdtBridge'
import { entityRegistry } from './entityRegistry'
import { API_BASE_URL, WEBSOCKET_URL } from '../api/config'
import { wsClient, WSClientDocMismatchError, type WSClientHandle } from '../services/yjs/wsClientSingleton'
import { createOverflowDetector } from '../services/yjs/wsOverflowRecovery'
import { shouldSelfHealOrgDoc } from '../services/yjs/orgDocBloatGuard'
import { _DEFAULT_HARD_LIMITS_BY_PREFIX } from '../services/yjs/wsSizeBudget'
import { onHardStop, isHardStopped, type HardStopEvent } from '../services/yjs/wsHardStop'
import { wsReconnectCoordinator } from '../services/wsReconnectCoordinator'
import { createFileLogger } from '../utils/logger'
import useUserStore from './userStore'
import useSpaceStore from './spaceStore'
import { getCurrentOrgId } from './dataspaceContextStore'

const log = createFileLogger('ORG_YDOC_STORE', { enabled: false })

interface OrgYjsDebugState {
  dataspaceId: string | null
  wsConnected: boolean
  wsSynced: boolean
  wsUrl: string | null
  edgeCount: number
  nodeCount: number
  lastUpdateAt: number | null
}

let _debugState: OrgYjsDebugState = {
  dataspaceId: null,
  wsConnected: false,
  wsSynced: false,
  wsUrl: null,
  edgeCount: 0,
  nodeCount: 0,
  lastUpdateAt: null,
}

export function getOrgYDocDebugState(): OrgYjsDebugState {
  return { ..._debugState }
}

// ── Connection status (for a lightweight UI indicator) ───────────────────────
// Mirrors y-websocket's own status values so callers don't need a translation
// layer. 'disconnected' covers both "never connected yet" and "was connected,
// dropped" — the UI indicator only needs to know "can I trust what I'm
// looking at right now", not the full history.
export type OrgConnectionStatus = 'connecting' | 'connected' | 'disconnected'

let _connectionStatus: OrgConnectionStatus = 'disconnected'
const _connectionStatusListeners = new Set<() => void>()

function _setConnectionStatus(status: OrgConnectionStatus): void {
  if (_connectionStatus === status) return
  _connectionStatus = status
  _connectionStatusListeners.forEach(fn => fn())
}

/** Current org-room WS connection status. Safe to call at any time. */
export function getOrgConnectionStatus(): OrgConnectionStatus {
  return _connectionStatus
}

/** Subscribe to org-room WS connection status changes. Returns an unsubscribe function. */
export function subscribeToOrgConnectionStatus(cb: () => void): () => void {
  _connectionStatusListeners.add(cb)
  return () => _connectionStatusListeners.delete(cb)
}

// ── Synced dimension (for the global connection indicator) ──────────────────
// `_connectionStatus === 'connected'` only means the socket opened — a
// "connected zombie" (see wsReconnectCoordinator's zombie detection) reports
// exactly that while never completing the Y.js sync handshake. A UI indicator
// that only looks at connection status can say "connected" for a room that's
// actually delivering nothing. This tracks the Hocuspocus 'sync' event
// separately so callers can require BOTH dimensions.
let _connectionSynced = false
const _connectionSyncedListeners = new Set<() => void>()

function _setConnectionSynced(synced: boolean): void {
  if (_connectionSynced === synced) return
  _connectionSynced = synced
  _connectionSyncedListeners.forEach(fn => fn())
}

/** Whether the org room has completed at least one sync since its current connection attempt. */
export function getOrgConnectionSynced(): boolean {
  return _connectionSynced
}

/** Subscribe to org-room synced-state changes. Returns an unsubscribe function. */
export function subscribeToOrgConnectionSynced(cb: () => void): () => void {
  _connectionSyncedListeners.add(cb)
  return () => _connectionSyncedListeners.delete(cb)
}

let _ydoc: Y.Doc | null = null
let _ws: WebsocketProvider | null = null
let _wsHandle: WSClientHandle | null = null
let _currentDataspaceId: string | null = null
let _readyResolvers: Array<{ resolve: () => void; reject: (err: Error) => void }> = []
let _isReady = false

// ── Hard-stop self-heal bookkeeping ─────────────────────────────────────────
// See the onHardStop subscription in initOrgYDocStore for the recovery logic.
// Attempts are counted per-dataspace and reset on a successful sync so a
// long-lived healthy session doesn't inherit an old org's near-exhausted
// budget, while a genuinely, persistently oversized org still gets bounded
// (not infinite) auto-recovery attempts.
let _unsubscribeHardStop: (() => void) | null = null
let _hardStopRecoveryDataspaceId: string | null = null
let _hardStopRecoveryAttempts = 0
const MAX_HARD_STOP_RECOVERY_ATTEMPTS = 3
const HARD_STOP_VERIFY_GRACE_MS = 3_000

// ── pendingCmds re-scan bookkeeping ──────────────────────────────────────────
// See the useSpaceStore.subscribe call in initOrgYDocStore for why a re-scan
// (not just the Yjs 'add'-event observer) is needed.
let _unsubscribeSpaceChange: (() => void) | null = null
let _lastRescanActiveSpaceId: string | null = null

// ── Proactive bloat self-heal (see orgDocBloatGuard.ts) ─────────────────────
// A long-lived tab accumulates unbounded CRDT struct history (entity
// renames, ghost-job tombstones, pendingCmds churn — each its own
// transaction) even though the room's LIVE content never grows. Reconnecting
// to a server room that was independently re-seeded (idle eviction, deploy)
// then forces the client to send that ENTIRE history in one SyncStep2 frame,
// which can dwarf the live content and trip the server's payload budget —
// confirmed in production (org:69bb4cbf42fc3c6460077f89: 0.73 MiB canonical
// vs. 16.9-17.8 MiB reconnect frames, repeatedly, over 16 MiB). Self-healing
// well below that ceiling means the local doc never accumulates enough
// history to be at risk, independent of whatever reactive guard (wsSizeBudget,
// wsHardStop) might or might not catch a given reconnect in time.
//
// Threshold: 1/5 of the org: room's own hard payload budget — generous
// headroom over the ~1 MiB typical live content, far under the ceiling this
// exists to keep the doc away from.
const ORG_DOC_BLOAT_THRESHOLD_BYTES = Math.floor((_DEFAULT_HARD_LIMITS_BY_PREFIX['org:'] ?? 16 * 1024 * 1024) / 5)
const ORG_DOC_BLOAT_CHECK_INTERVAL_MS = 15 * 60 * 1000 // 15 minutes
let _bloatCheckTimer: ReturnType<typeof setInterval> | null = null

// A backgrounded tab's timers (including _bloatCheckTimer above) are throttled
// or fully suspended by the browser, so a doc can cross the threshold while
// hidden and go unchecked for far longer than ORG_DOC_BLOAT_CHECK_INTERVAL_MS.
// The 'disconnected' hook alone doesn't cover this either: a resumed tab often
// finds its socket still nominally open and races straight into a reconnect
// before ever transitioning through 'disconnected'. Checking on the visible
// transition itself — before any of that pent-up reconnect activity — closes
// that gap. No grace delay here (unlike wsObservability's visibility-restore
// recheck): a bloat check is instantaneous and always safe to run immediately,
// there's no "just reconnected normally" false-positive to wait out.
let _bloatVisibilityHandler: (() => void) | null = null

type EntityChangeCallback = (action: 'add' | 'update' | 'delete', entityId: string, data?: any) => void
const _entityListeners = new Set<EntityChangeCallback>()

type ReconnectCallback = () => void
const _reconnectListeners = new Set<ReconnectCallback>()

export function subscribeToOrgReconnect(cb: ReconnectCallback): () => void {
  _reconnectListeners.add(cb)
  return () => _reconnectListeners.delete(cb)
}


function _notifyReadyWaiters(): void {
  _isReady = true
  const waiters = _readyResolvers.splice(0)
  waiters.forEach(({ resolve }) => resolve())
}

/**
 * Proactively recycle the local org Y.Doc if its accumulated CRDT history
 * has grown past ORG_DOC_BLOAT_THRESHOLD_BYTES — see the bookkeeping comment
 * above and orgDocBloatGuard.ts for the full rationale. No-op below the
 * threshold, so this is safe to call unconditionally and often (periodic
 * timer, every 'disconnected' transition).
 */
function _checkOrgDocBloat(dataspaceId: string): void {
  if (!_ydoc || _currentDataspaceId !== dataspaceId) return
  if (!shouldSelfHealOrgDoc(_ydoc, ORG_DOC_BLOAT_THRESHOLD_BYTES)) return
  console.warn(
    `[OrgYDoc] ⚠️ Local Y.Doc for ...${dataspaceId.slice(-6)} has grown past ` +
    `${(ORG_DOC_BLOAT_THRESHOLD_BYTES / 1024 / 1024).toFixed(2)}MiB of accumulated CRDT history ` +
    `— self-healing now, before a reconnect's full resync could risk the server's payload budget`
  )
  destroyOrgYDocStore()
  initOrgYDocStore(dataspaceId)
}

/**
 * Ensure the org Y.Doc room is connected for `dataspaceId`. Safe to call on
 * every Space load — a fast no-op when already connected to the right
 * dataspace, otherwise (re)connects.
 *
 * Exists because nothing previously enforced that the org room tracks the
 * Space actually being viewed: `initOrgYDocStore` is only ever invoked from
 * `dataspaceContextStore`'s `setContext`/`applyRemoteContextChange`, keyed on
 * the *dataspace context's* org, not the open Space's `org_id`. Those two
 * can silently diverge — a stale persisted context, a direct deep link into
 * a Space belonging to a different org than the current context, or the org
 * room's own hard-stop/overflow self-heal exhausting its retry budget and
 * leaving the tab permanently disconnected for the rest of the session — and
 * when they do, every org-wide broadcast (space_open_window and friends via
 * `desktop_open_window`, sidebar tree updates, membership events, ...) is
 * silently dropped with `{ dropped: true }`, because no replica anywhere
 * holds the room this Space actually needs. See
 * `docs/... FIX_DESKTOP_OPEN_WINDOW` investigation: a live session was found
 * connected to a completely unrelated org's room while its own Space's org
 * room had no connection at all — 100% of `desktop_open_window` calls
 * dropped for the entire session, with no diagnostic anywhere.
 *
 * Deliberately does NOT touch `dataspaceContextStore.currentContext` — that
 * drives billing attribution and the org-switcher UI, and silently flipping
 * it as a side effect of opening a Space would be a much bigger behavioral
 * change than "make this Space's command delivery work". Call sites that
 * also care about the context itself being wrong should check
 * `getOrgYDocDebugState().dataspaceId` against `getCurrentOrgId()` separately.
 */
// Mirrors websocket-server/src/hocuspocus/roomAuth.js's OBJECTID_RE (acl.js).
// An org room is ALWAYS keyed by a MongoDB ObjectId — the orgMembership auth
// strategy denies anything else. Guarding it here too (not just server-side)
// means a caller that resolves the wrong id (e.g. a user id instead of an
// org id — see FIX_DESKTOP_OPEN_WINDOW investigation) fails loudly on this
// tab instead of silently tearing down a working room to join one the server
// will never allow.
const ORG_ROOM_OBJECTID_RE = /^[0-9a-fA-F]{24}$/

function isValidOrgRoomId(dataspaceId: string): boolean {
  return ORG_ROOM_OBJECTID_RE.test(dataspaceId)
}

export function ensureOrgYDocForDataspace(dataspaceId: string): void {
  if (!dataspaceId) return
  if (!isValidOrgRoomId(dataspaceId)) {
    console.error(
      `[OrgYDoc] ⚠️ ensureOrgYDocForDataspace called with invalid dataspaceId "${dataspaceId}" ` +
      `(not a 24-hex ObjectId) — refusing to join it. The server would permanently deny this room, ` +
      `so any existing org room connection (...${_currentDataspaceId ? _currentDataspaceId.slice(-6) : 'none'}) is left in place.`
    )
    return
  }
  if (_currentDataspaceId === dataspaceId && _ws) return
  if (_currentDataspaceId && _currentDataspaceId !== dataspaceId) {
    console.error(
      `[OrgYDoc] ⚠️ MISMATCH: org room was connected for ...${_currentDataspaceId.slice(-6)} but the Space ` +
      `being opened belongs to ...${dataspaceId.slice(-6)} — every org-wide broadcast to this Space ` +
      `(desktop_open_window and friends, sidebar tree updates, ...) was silently targeting the wrong room. ` +
      `Reconnecting to the correct dataspace now.`
    )
  } else {
    console.log(`[OrgYDoc] ensureOrgYDocForDataspace: not connected — initializing for ...${dataspaceId.slice(-6)}`)
  }
  initOrgYDocStore(dataspaceId)
}

// A command ack is only ever read by the handful of ~8-15s polls tool-runtime's
// broadcastAndVerify runs right after sending the command it belongs to (see
// fetchOrgCmdAck in space_ui.ts) — nothing reads it after that. Evicting on a
// short timer keeps `cmdAcks` from accumulating for the life of the room,
// mirroring pendingCmds' own delete-after-use lifecycle.
const CMD_ACK_TTL_MS = 30_000

// Lazily-resolved reference to conversationYjsStore's hasActiveRunForSpace,
// used by the pause-exemption predicate below. A dynamic import (not a static
// one) deliberately: conversationYjsStore pulls in the entire SpaceChat store
// module graph (chatActivityStore, thinkingLoaderStore, ...), which is far
// heavier than anything else this module touches — a static import here would
// make every org-room init pay that cost up front for a check that's only
// ever consulted at long-hide-pause time. `null` (not yet resolved) makes the
// predicate a safe, non-exempting no-op for the brief window before the first
// initOrgYDocStore call's import settles.
let _hasActiveRunForSpaceFn: ((spaceId: string) => boolean) | null = null

/**
 * Write the result of a broadcast-dispatched command back into the org Y.Doc
 * so tool-runtime can confirm real success/failure instead of inferring it by
 * polling an unrelated room's state (see fetchOrgCmdAck/broadcastAndVerify in
 * space_ui.ts). Keyed by the same `id` the command itself was stored under in
 * `pendingCmds`. Only the tab that actually ran the handler calls this — a tab
 * that gated out (wrong targetUserId/targetSpaceId) or found the command stale
 * does not, leaving room for a different, matching tab to report the outcome.
 */
function writeCmdAck(
  id: string,
  ack: { ok: boolean; message?: string; error?: string; data?: unknown },
): void {
  if (!_ydoc) return
  const doc = _ydoc
  const cmdAcksMap = doc.getMap<string>('cmdAcks')
  doc.transact(() => {
    cmdAcksMap.set(id, JSON.stringify({ ...ack, ts: Date.now() }))
  })
  setTimeout(() => {
    if (_ydoc !== doc) return // room was torn down/replaced — nothing to evict
    doc.transact(() => { cmdAcksMap.delete(id) })
  }, CMD_ACK_TTL_MS)
}

export function initOrgYDocStore(dataspaceId: string): void {
  if (!dataspaceId) return
  if (!isValidOrgRoomId(dataspaceId)) {
    console.error(
      `[OrgYDoc] ⚠️ Refusing to init org room for invalid dataspaceId "${dataspaceId}" ` +
      `(not a 24-hex ObjectId) — the server would permanently deny this room. ` +
      `Leaving any existing org room connection (...${_currentDataspaceId ? _currentDataspaceId.slice(-6) : 'none'}) in place.`
    )
    return
  }

  if (_currentDataspaceId === dataspaceId && _ws) {
    console.log(`[OrgYDoc] Already connected for dataspace ...${dataspaceId.slice(-6)}`)
    return
  }

  destroyOrgYDocStore()

  console.log(`[OrgYDoc] Initializing for dataspace ...${dataspaceId.slice(-6)}`)

  _currentDataspaceId = dataspaceId
  _isReady = false
  _debugState = {
    dataspaceId,
    wsConnected: false,
    wsSynced: false,
    wsUrl: WEBSOCKET_URL,
    edgeCount: 0,
    nodeCount: 0,
    lastUpdateAt: null,
  }

  _ydoc = new Y.Doc({ guid: `org:${dataspaceId}` })
  const edgesMap = _ydoc.getMap<string>('edges')
  const nodesMap = _ydoc.getMap<string>('nodes')
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')

  // Track command IDs already executed by this tab so that CRDT propagation delays
  // between tabs — or a re-scan re-visiting an entry the Yjs observer already ran —
  // don't cause the same non-idempotent command to execute twice.
  const _executedCmdIds = new Set<string>()

  // Generous relative to the old 5s: `ts` is stamped by the tool-runtime
  // container's clock (nodeContext.ts) and compared here against the
  // browser's own clock — no cross-machine clock sync is assumed, so this
  // exists only to bound truly ancient, no-longer-relevant commands, not to
  // draw a tight correctness line. 5s was tight enough that ordinary clock
  // drift (routine after a Docker Desktop host sleep/resume in local dev) or
  // a reconnect gap of a few seconds silently discarded the command with no
  // diagnostic — see the "dropped: stale" warning below, added alongside
  // this widening. A generous, uniform window also makes deferred delivery
  // (see _rescanPendingCmds below) actually useful: a tab that reconnects or
  // switches back to the target Desktop tens of seconds later still runs the
  // command instead of finding it already aged out.
  const PENDING_CMD_STALE_MS = 60_000

  /**
   * Process one `pendingCmds` entry: parse, apply the targetUserId/targetSpaceId
   * routing gates, check staleness, and execute the command's handler —
   * writing the result back as a cmdAck (see writeCmdAck). Shared between the
   * Yjs 'add'-event observer (below) and `_rescanPendingCmds` (re-examines
   * every current entry, not just newly-added ones) so a cmd this tab left in
   * place because it didn't match at the time — a different Desktop was
   * active, or the room was mid-reconnect — still runs once conditions change,
   * instead of only ever being reconsidered on its one original Yjs event.
   * Pushes `id` onto `toDelete` for the caller to batch-delete outside this
   * pass (never deletes inline — avoids a re-entrant transaction while this
   * same event/scan is still iterating the map).
   */
  function _processPendingCmd(id: string, toDelete: string[]): void {
    if (_executedCmdIds.has(id)) return
    const raw = pendingCmdsMap.get(id)
    if (!raw) {
      toDelete.push(id) // empty entry — clean it up regardless of target
      return
    }
    let parsed: any
    try {
      parsed = JSON.parse(raw)
    } catch {
      toDelete.push(id) // malformed JSON — clean up so it doesn't accumulate
      return
    }
    try {
      const { name, params, ts, targetUserId, targetSpaceId } = parsed
      const myUserId = useUserStore.getState().auth0Id ?? null
      const myActiveSpaceId = useSpaceStore.getState().activeSpace?.id ?? null
      // Per-user routing: cmds stamped with targetUserId only execute on that
      // user's tabs. Without this gate, every member of the org room would
      // execute UI commands like space_open_window — a teammate would see
      // your AI-created doc pop open in their UI. Tabs that are NOT the
      // target leave the cmd in place so the targeted user's tab can still
      // pick it up (via a re-scan, if not already connected); the target's
      // tab is responsible for cleanup. If no target's tab ever comes online
      // the cmd ages out via the staleness window below (and pendingCmds
      // isn't persisted, so it dies with the room regardless).
      if (targetUserId) {
        if (!myUserId || myUserId !== targetUserId) {
          console.debug(`[OrgYDoc] pendingCmd ${name} gated: targetUserId=${targetUserId} !== this tab's ${myUserId ?? '(none)'}`)
          return
        }
      }
      // Per-Desktop routing: cmds stamped with targetSpaceId only execute in
      // the tab whose Desktop (Space) matches. A user can have several
      // Desktops open in separate tabs at once; without this gate, an
      // AI-triggered UI command like space_open_window would fire in every
      // open Desktop tab instead of just the one the agent/chat is running
      // in. Same leave-in-place semantics as the targetUserId gate above.
      if (targetSpaceId) {
        if (myActiveSpaceId !== targetSpaceId) {
          console.debug(`[OrgYDoc] pendingCmd ${name} gated: targetSpaceId=${targetSpaceId} !== this tab's active Desktop ${myActiveSpaceId ?? '(none)'}`)
          return
        }
      }
      // Schedule deletion outside this pass to avoid a re-entrant transaction.
      toDelete.push(id)
      if (Date.now() - ts > PENDING_CMD_STALE_MS) {
        // A command that reached this tab (passed both routing gates above)
        // but aged out before being processed is a real, diagnosable drop,
        // not routine cross-tab noise like the gates above.
        console.warn(`[OrgYDoc] pendingCmd ${name} dropped: stale (age=${Date.now() - ts}ms > ${PENDING_CMD_STALE_MS}ms)`)
        return
      }
      _executedCmdIds.add(id)
      // Evict oldest half instead of clearing entirely — a full clear would forget
      // recently-executed IDs and allow re-execution of replayed CRDT events.
      if (_executedCmdIds.size > 500) {
        const toEvict = [..._executedCmdIds].slice(0, 250)
        toEvict.forEach(evictId => _executedCmdIds.delete(evictId))
      }
      console.log(`[OrgYDoc] pendingCmd: ${name}${params ? ` params=${JSON.stringify(params).slice(0, 160)}` : ''}`)
      if (name === 'video_studio_progress' || name === 'video_studio_result') {
        console.log(`[VS/Generate] [OrgYDoc] received ${name}`, params)
      }

      // Infrastructure resync command — re-seeds the sidebar tree from the
      // org Y.Doc snapshot (with MongoDB fallback). Emitted by bulk ingest
      // after each ~500-message page so the browser sees new threads without
      // receiving per-entity CRDT floods.
      if (name === 'orgTreeResync') {
        const targetId = (params as any)?.dataspaceId || _currentDataspaceId
        if (targetId) {
          import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
            useDataspaceTreeStore.getState().initialize(targetId)
          }).catch(() => {})
        }
        return
      }

      // The websocket server's mass-delete guard refused to persist a batch
      // of deletes from this (or another) tab. The Y.Map locally has the
      // edges removed (the observer ran before the guard tripped) but Mongo
      // is untouched — so we reload the tree from the authoritative server
      // state and toast the user. Without this surface the user sees their
      // sidebar entries reappear silently on the next page load with no
      // explanation.
      if (name === 'org_mass_delete_refused') {
        const p = params as any
        const targetId = p?.dataspaceId || _currentDataspaceId
        Promise.all([
          import('./dataspaceTreeStore'),
          import('../services/ui/toastService'),
        ]).then(([treeMod, toastMod]) => {
          toastMod.toastService.show(
            'Bulk delete refused: the server detected an unusually large deletion (likely a sync glitch) and reverted it. Try again on a smaller selection.',
            'warning',
            { duration: 8000 },
          )
          if (targetId) {
            treeMod.useDataspaceTreeStore.getState().initialize(targetId)
          }
        }).catch(() => {})
        return
      }

      if (name === 'org_membership_revoked') {
        const p = params as any
        Promise.all([
          import('./userStore' as string),
          import('./dataspaceContextStore'),
          import('../services/ui/toastService'),
          import('../services/spaceHelpers'),
        ]).then(async ([userStoreMod, dsMod, toastMod, spaceHelpersMod]) => {
          const myUserId2 = (userStoreMod.default?.getState?.() as any)?.auth0Id
          if (!myUserId2 || myUserId2 !== p?.user_id) return
          const orgName = p?.org_name || 'an organization'
          toastMod.toastService.show(`You were removed from ${orgName}`, 'warning', { duration: 6000 })
          await dsMod.useDataspaceContextStore.getState().switchToPersonal()
          try {
            window.location.href = await spaceHelpersMod.resolveLandingDesktopRoute()
          } catch (err) {
            log.error('[org_membership_revoked] Failed to resolve landing Desktop, reloading in place:', err)
            window.location.reload()
          }
        }).catch(() => {})
        return
      }

      if (name === 'org_membership_added') {
        const p = params as any
        Promise.all([
          import('./dataspaceContextStore'),
          import('../services/ui/toastService'),
        ]).then(([dsMod, toastMod]) => {
          const orgName = p?.org_name || 'a new workspace'
          // Reload the org list so the new workspace appears in the sidebar immediately.
          dsMod.useDataspaceContextStore.getState().loadUserOrgs()
          toastMod.toastService.show(
            `You've been added to ${orgName}. Switch to it from the menu.`,
            'success',
            { duration: 8000 },
          )
        }).catch(() => {})
        return
      }

      // ACLService.add_permission/remove_permission (backend) broadcasts this
      // after every successful ACL write on an entity — see _notify_acl_change
      // in services/acl_service.py. Stamped with targetUserId (unless
      // subject_user_id is "public"), so the targetUserId gate above already
      // ensures only the affected user's tabs reach this handler. Drops the
      // entity from every local read cache so a revoked/downgraded user's
      // sidebar, Drive view, and any open window don't keep showing a stale
      // copy — the next access attempt (if any) re-fetches and gets a fresh
      // 403/404 or the new, more restricted role from the server.
      if (name === 'entity_acl_revoked') {
        const p = params as any
        const entityId = p?.entity_id
        if (!entityId) return
        entityRegistry.deleteEntity(entityId)
        import('./entityByIdCache').then(({ useEntityByIdCache }) => {
          useEntityByIdCache.getState().remove(entityId)
        }).catch(() => {})
        return
      }

      import('./commandRegistryStore').then(({ useCommandRegistryStore }) => {
        const registry = useCommandRegistryStore.getState()
        // Cross-tab command broadcasts are best-effort: another tab (or device) may be
        // running a newer/older app version that knows about commands this one doesn't.
        // Silently drop unknown commands rather than throwing, which otherwise surfaces
        // as a top-level React error.
        const isKnown = registry.commands.has(name as any) || registry.commandDefinitions.has(name as any)
        if (!isKnown) {
          console.debug(`[orgYDocStore] Ignoring cross-tab command unknown to this build: ${name}`)
          return
        }
        // Mark every broadcast-dispatched command as such: the entities it
        // refers to were already created/updated server-side (or by another
        // tab) before this broadcast was sent, so a handler MUST treat this
        // as "sync your UI to reflect what already happened" — never as a
        // fresh request to redo the underlying write. Commands that skip
        // this distinction (e.g. re-invoking the same tool that triggered
        // the broadcast) create a self-sustaining request→broadcast→request
        // feedback loop. See browser_open_tab in windowManagement.ts.
        //
        // The result used to be discarded here (`.catch(() => {})` covered
        // only the dynamic import, not this call) — "Entity not found," a
        // thrown exception, and a genuine successful open were all
        // indistinguishable to whatever polled for the outcome. Writing it
        // back as an ack is what lets tool-runtime's broadcastAndVerify
        // confirm real success/failure in one round-trip instead of
        // guessing from an unrelated room's state.
        registry.executeCommand(name, params ?? {}, { fromBroadcast: true })
          .then((result: any) => {
            const ok = result?.status !== 'error'
            writeCmdAck(id, {
              ok,
              message: typeof result?.message === 'string' ? result.message : undefined,
              error: ok ? undefined : (result?.error ?? result?.message ?? 'Command failed'),
              data: result?.data,
            })
          })
          .catch((err: any) => {
            writeCmdAck(id, { ok: false, error: err?.message ?? String(err) })
          })
      }).catch(() => {})
    } catch {
      // Unexpected error after parse (e.g. destructure on a non-object) —
      // schedule cleanup so the entry doesn't loop forever. push twice is
      // harmless: pendingCmdsMap.delete(id) is idempotent.
      toDelete.push(id)
    }
  }

  function _flushPendingCmdDeletes(toDelete: string[]): void {
    if (toDelete.length === 0) return
    queueMicrotask(() => {
      if (!_ydoc) return
      _ydoc.transact(() => {
        toDelete.forEach(deleteId => pendingCmdsMap.delete(deleteId))
      })
    })
  }

  /**
   * Re-examine every CURRENT `pendingCmds` entry, not just ones the Yjs
   * observer just saw an 'add' event for. Needed because the observer's
   * `event.changes.keys` only fires for entries that are new to THIS tab's
   * local doc — an entry left in place by the targetUserId/targetSpaceId
   * gates in `_processPendingCmd` never re-fires on its own once the gate
   * condition that blocked it changes (the user switches back to the target
   * Desktop, or this tab reconnects after the target tab was offline).
   * Called on org-room (re)sync and on active-Desktop change — see call
   * sites below.
   */
  function _rescanPendingCmds(): void {
    const toDelete: string[] = []
    pendingCmdsMap.forEach((_raw, id) => _processPendingCmd(id, toDelete))
    _flushPendingCmdDeletes(toDelete)
  }

  // Re-scan whenever the user switches Desktops in this tab — the targetSpaceId
  // gate above only re-evaluates a given entry once, so without this, opening
  // an entity via chat in Desktop A while looking at Desktop B, then switching
  // to Desktop A, would never pick up the deferred command. Plain zustand (no
  // subscribeWithSelector middleware) fires on every store change, so the
  // activeSpace id is diffed manually against the last value this tab acted on.
  _unsubscribeSpaceChange = useSpaceStore.subscribe((state) => {
    const activeSpaceId = state.activeSpace?.id ?? null
    if (activeSpaceId === _lastRescanActiveSpaceId) return
    _lastRescanActiveSpaceId = activeSpaceId
    _rescanPendingCmds()
  })

  const roomName = `org:${dataspaceId}`

  // Evict any stale WSClient entry for this room. This handles the Vite HMR
  // case: the wsClient singleton lives on globalThis and survives module
  // re-execution, but the module-level _wsHandle is reset to null on reload.
  // destroyOrgYDocStore() had no handle to close, so the old entry (with the
  // old Y.Doc) is still registered. Without eviction the open() below would
  // throw WSClientDocMismatchError and leave the store half-initialized,
  // causing a persistent crash loop on every subsequent render.
  wsClient.evictStaleEntry(roomName, _ydoc)

  // resyncInterval is a no-op here: the transport is HocuspocusWebsocketProvider
  // (not raw y-websocket), which accepts this option only for signature parity
  // and manages resync internally — it never schedules the periodic full-state
  // re-upload y-websocket used to. Passed through anyway for clarity/parity with
  // other WSClient.open() call sites.
  try {
    _wsHandle = wsClient.open({
      roomname: roomName,
      doc: _ydoc,
      connect: true,
      resyncInterval: -1,
      maxBackoffTime: 30_000,
    })
  } catch (err) {
    // Clean up the Y.Doc we just created so the store is not left in a
    // half-initialized state (_currentDataspaceId set but no provider). The
    // eviction above makes WSClientDocMismatchError effectively unreachable;
    // any throw here is an unexpected provider-construction failure, which we
    // surface (re-throw) rather than mask — but only after restoring a clean,
    // uninitialized state so the next render can retry from scratch.
    _ydoc.destroy()
    _ydoc = null
    _currentDataspaceId = null
    _isReady = false
    _debugState = { dataspaceId: null, wsConnected: false, wsSynced: false, wsUrl: null, edgeCount: 0, nodeCount: 0, lastUpdateAt: null }
    if (err instanceof WSClientDocMismatchError) {
      console.error('[OrgYDoc] WSClientDocMismatchError after eviction — should not happen', err)
    }
    throw err
  }
  _ws = _wsHandle.provider

  // Exempt this room from the long-hide pause while an agent run targeting
  // this tab's active Desktop is in flight. Without this, a tab backgrounded
  // >30s mid-run has its org room torn down by wsReconnectCoordinator.pauseAll()
  // — the exact room desktop_open_window/broadcastCmd deliver commands and read
  // cmdAcks through (see writeCmdAck above) — so a run's UI commands are
  // dropped/never acked purely because the user looked away. Mirrors the
  // conversation room's own exemption (see conversationYjsStore.ts). Cleared
  // automatically on unregister (destroyOrgYDocStore → wsHandle.close() →
  // tokenRefresher.unregister → wsReconnectCoordinator.unregister).
  if (_hasActiveRunForSpaceFn === null) {
    import('../components/Space/SpaceChat/store/conversationYjsStore')
      .then(({ hasActiveRunForSpace }) => { _hasActiveRunForSpaceFn = hasActiveRunForSpace })
      .catch((err) => console.warn('[OrgYDoc] Failed to load hasActiveRunForSpace for pause exemption:', err))
  }
  wsReconnectCoordinator.setPauseExemption(
    _ws,
    () => _hasActiveRunForSpaceFn !== null && _hasActiveRunForSpaceFn(useSpaceStore.getState().activeSpace?.id ?? ''),
  )

  // Idle-connection keepalive (arming y-protocols' awareness self-refresh so
  // this otherwise presence-free room stays inside the server's 30s idle
  // timeout) is now handled centrally by HocuspocusWebsocketProvider itself
  // on every room — see armKeepalive() in hocuspocusWebsocketProvider.ts.
  // A well-behaved keepalive means a healthy connection can in principle
  // never disconnect for days — so the bloat guard below also needs this
  // periodic timer, not just the 'disconnected' hook further down, to bound
  // worst-case growth independent of connection stability.
  _bloatCheckTimer = setInterval(() => _checkOrgDocBloat(dataspaceId), ORG_DOC_BLOAT_CHECK_INTERVAL_MS)
  if (typeof document !== 'undefined') {
    _bloatVisibilityHandler = () => {
      if (document.visibilityState === 'visible') _checkOrgDocBloat(dataspaceId)
    }
    document.addEventListener('visibilitychange', _bloatVisibilityHandler)
  }

  // ── Overflow circuit-breaker detection ──────────────────────────────────────
  // See wsOverflowRecovery.ts for the detection heuristic (size-gated, shared
  // with conversationYjsStore). Recovery itself — destroy + re-init the local
  // Y.Doc rather than reload the page — stays here because it's store-specific:
  // a page reload loops if the server's circuit is still open (HTTP 503 → 1006
  // → reload → ∞), while a targeted reset produces an empty Y.Doc which cannot
  // overflow, letting the provider back off until the server-side circuit
  // window expires.
  const _capturedDataspaceId = dataspaceId
  const _overflowDetector = createOverflowDetector(_ws, {
    // Half of the room's own hard payload budget (see wsSizeBudget) — a doc
    // this far under budget cannot be the overflow the breaker exists to catch.
    sizeThresholdBytes: (_DEFAULT_HARD_LIMITS_BY_PREFIX['org:'] ?? 16 * 1024 * 1024) / 2,
    getDocBytes: () => (_ydoc ? Y.encodeStateAsUpdateV2(_ydoc).byteLength : 0),
    onOverflow: () => {
      console.error(
        `[OrgYDoc] ⚠️ Payload overflow detected for ...${_capturedDataspaceId.slice(-6)} ` +
        `— destroying local Y.Doc and reconnecting fresh in 5s`
      )
      destroyOrgYDocStore()
      setTimeout(() => {
        if (_currentDataspaceId !== null && _currentDataspaceId !== _capturedDataspaceId) return
        initOrgYDocStore(_capturedDataspaceId)
      }, 5000)
    },
  })

  // ── Hard-stop self-heal (genuine oversized org room) ────────────────────────
  // wsSizeBudget verifies its own trips off-thread and clears itself within
  // ~1s if a 'size_budget' hard-stop was a false positive from unmerged
  // update-log growth (see wsSizeBudget.ts) — most org-room trips resolve
  // right there with no action needed here.
  //
  // If the room is STILL hard-stopped after that verification window (a
  // confirmed-oversized 'size_budget', or a server-confirmed
  // 'oversized_payload'), the doc is genuinely too large for THIS tab's
  // session state. Destroy the local copy and re-init from scratch, exactly
  // like `_overflowDetector.onOverflow` above: a fresh Y.Doc streams the
  // server's compacted canonical snapshot, almost always well under budget
  // even when this tab's accumulated session state was not. This is the same
  // recovery a manual page reload performs — done automatically, so the user
  // never sees "Offline" hang around waiting for them to notice and reload.
  // Bounded so a truly, persistently oversized org doesn't loop forever.
  if (_hardStopRecoveryDataspaceId !== dataspaceId) {
    _hardStopRecoveryDataspaceId = dataspaceId
    _hardStopRecoveryAttempts = 0
  }
  _unsubscribeHardStop = onHardStop((event: HardStopEvent) => {
    if (event.provider !== _ws) return
    if (event.cause !== 'size_budget' && event.cause !== 'oversized_payload') return
    const capturedDataspaceId = dataspaceId
    setTimeout(() => {
      if (_currentDataspaceId !== capturedDataspaceId) return
      if (!_ws || !isHardStopped(_ws)) return // verification already cleared it — nothing to do
      if (_hardStopRecoveryAttempts >= MAX_HARD_STOP_RECOVERY_ATTEMPTS) {
        console.error(
          `[OrgYDoc] ⚠️ Hard-stop recovery exhausted (${MAX_HARD_STOP_RECOVERY_ATTEMPTS} attempts) for ` +
          `...${capturedDataspaceId.slice(-6)} — giving up auto-recovery, a page reload is required`
        )
        return
      }
      _hardStopRecoveryAttempts++
      console.error(
        `[OrgYDoc] ⚠️ Confirmed oversized org room (cause=${event.cause}) for ...${capturedDataspaceId.slice(-6)} ` +
        `(attempt ${_hardStopRecoveryAttempts}/${MAX_HARD_STOP_RECOVERY_ATTEMPTS}) — destroying local Y.Doc and reconnecting fresh`
      )
      destroyOrgYDocStore()
      initOrgYDocStore(capturedDataspaceId)
    }, HARD_STOP_VERIFY_GRACE_MS)
  })

  _setConnectionStatus('connecting')

  let _prevWsStatus: string | null = null
  _ws.on('status', ({ status }: { status: string }) => {
    _debugState.wsConnected = status === 'connected'
    console.log(`[OrgYDoc] WS ${status} for dataspace ...${dataspaceId.slice(-6)}`)
    if (status === 'connecting' || status === 'connected' || status === 'disconnected') {
      _setConnectionStatus(status)
    }
    // Any transition away from a live, synced socket invalidates the last sync —
    // a fresh connect() always needs its own 'sync' event before data can be trusted.
    if (status !== 'connected') _setConnectionSynced(false)
    // Re-attach the close listener whenever y-websocket creates a new socket
    // (the underlying ws is recreated on every reconnect).
    if (status === 'connecting') _overflowDetector.attachToCurrentSocket()
    // Disconnect is the natural moment right before the provider's own
    // reconnect attempt would fire the risky full-history resync (see
    // ORG_DOC_BLOAT_THRESHOLD_BYTES above) — check here as a complement to
    // the periodic timer, which alone wouldn't catch bloat between checks
    // on a connection that happens to drop right after crossing it.
    if (status === 'disconnected') _checkOrgDocBloat(dataspaceId)
    if (status === 'connected' && _prevWsStatus !== null && _prevWsStatus !== 'connected') {
      console.log(`[OrgYDoc] WS reconnected — notifying ${_reconnectListeners.size} listeners`)
      _reconnectListeners.forEach(fn => fn())
    }
    _prevWsStatus = status
  })

  _ws.on('sync', (synced: boolean) => {
    if (!synced) return
    _setConnectionSynced(true)
    // A real sync means the room is healthy again — don't let a resolved
    // incident count against the bounded hard-stop recovery budget above.
    _hardStopRecoveryAttempts = 0
    _debugState.wsSynced = true
    _debugState.edgeCount = edgesMap.size
    _debugState.nodeCount = nodesMap.size
    console.log(`[OrgYDoc] WS synced: ${edgesMap.size} edges, ${nodesMap.size} nodes for ...${dataspaceId.slice(-6)}`)
    // Perf instrumentation — note when the WS sync event arrives
    import('../utils/perfInstrumentation').then(({ perf }) => perf.mark('org_ydoc_sync')).catch(() => {})

    // A (re)sync can bring in pendingCmds entries this tab already had gated
    // out before a disconnect (e.g. targetSpaceId didn't match) — the Yjs
    // observer below only fires 'add' for entries new to this tab's local
    // doc, so those never get a second look on their own. See
    // _rescanPendingCmds's own doc comment for the full rationale.
    _rescanPendingCmds()

    // Unblock waiters as soon as WS sync arrives, before the full tree apply.
    // On tiny org docs (new-user signup: 3 edges / ~13b) waiters were previously
    // blocked for an extra macrotask + _applyFullTreeSync + _applyFullNodeSync
    // (~1-2s). Unblocking early lets spaceEntityStore Stage 2 proceed immediately
    // while the tree/node sync runs async in the background.
    _notifyReadyWaiters()

    // Defer the heavy tree/node sync off the Yjs message handler via setTimeout so the
    // browser can service other events (input, paint) instead of blocking for ~1.7s.
    // Capture the dataspaceId at closure time so we can bail out if the store was
    // destroyed and re-initialized for a different dataspace before the timeout fires.
    const syncedDataspaceId = dataspaceId
    setTimeout(() => {
      if (_currentDataspaceId !== syncedDataspaceId) {
        console.log(`[OrgYDoc] Deferred sync for ...${syncedDataspaceId.slice(-6)} discarded — dataspace changed`)
        return
      }
      Promise.all([
        _applyFullTreeSync(edgesMap),
        _applyFullNodeSync(nodesMap),
      ]).catch((err) => {
        console.warn('[OrgYDoc] Full sync apply failed:', err)
      })
    }, 0)
  })

  edgesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.edgeCount = edgesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleEdgeChanges(event, edgesMap)
  })

  nodesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.nodeCount = nodesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleNodeChanges(event, nodesMap)
  })

  pendingCmdsMap.observe((event: Y.YMapEvent<string>) => {
    const toDelete: string[] = []
    event.changes.keys.forEach((change, id) => {
      if (change.action !== 'add') return
      _processPendingCmd(id, toDelete)
    })
    _flushPendingCmdDeletes(toDelete)
  })

}

/**
 * Force-reconnect the org room's WS provider, bypassing every idempotence
 * guard along the way. Used by the chat-driven self-heal (see
 * wsRecoveryOnToolError.ts): when a space_ui tool reports "No active desktop
 * session" while THIS tab is actively rendering that failure over SSE, the
 * tab is provably alive — the org room's WS layer, which that check actually
 * depends on, is what's wedged. Falls back to a plain `connect()` if the
 * underlying provider doesn't expose `forceReconnect` (defensive; the
 * production provider always does).
 *
 * No-op if the store hasn't been initialized yet (nothing to reconnect).
 */
/**
 * Force-reconnects the org room — the self-heal path chat's
 * wsRecoveryOnToolError triggers on a "No active desktop session" tool
 * result. A blind reconnect of whatever room happens to be connected repeats
 * the FIX_DESKTOP_OPEN_WINDOW incident verbatim: the tab was connected to a
 * bogus, server-denied room, and reconnecting that same room just re-denies
 * it forever. Resolve the room this tab SHOULD be in first — the active
 * Space's own org_id, falling back to the dataspace context's org — and
 * retarget via ensureOrgYDocForDataspace (which also carries its own
 * ObjectId guard) whenever it differs from what's currently connected.
 */
export function forceReconnectOrgYDoc(): void {
  const activeSpace = useSpaceStore.getState().activeSpace
  const intendedDataspaceId = activeSpace?.org_id || getCurrentOrgId()

  if (intendedDataspaceId && intendedDataspaceId !== _currentDataspaceId) {
    console.error(
      `[OrgYDoc] forceReconnectOrgYDoc: currently connected to ...${_currentDataspaceId ? _currentDataspaceId.slice(-6) : 'none'} ` +
      `but the intended dataspace is ...${intendedDataspaceId.slice(-6)} (activeSpace.org_id=${activeSpace?.org_id ?? 'null'}, ` +
      `getCurrentOrgId()=${getCurrentOrgId() ?? 'null'}) — retargeting instead of reconnecting the wrong room.`
    )
    ensureOrgYDocForDataspace(intendedDataspaceId)
    return
  }

  if (!_ws) return
  const anyWs = _ws as unknown as { forceReconnect?: () => void }
  if (typeof anyWs.forceReconnect === 'function') {
    anyWs.forceReconnect()
  } else {
    _ws.connect()
  }
}

export function destroyOrgYDocStore(): void {
  if (_bloatCheckTimer !== null) {
    clearInterval(_bloatCheckTimer)
    _bloatCheckTimer = null
  }
  if (_bloatVisibilityHandler !== null) {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', _bloatVisibilityHandler)
    }
    _bloatVisibilityHandler = null
  }
  if (_unsubscribeHardStop) {
    _unsubscribeHardStop()
    _unsubscribeHardStop = null
  }
  if (_unsubscribeSpaceChange) {
    _unsubscribeSpaceChange()
    _unsubscribeSpaceChange = null
  }
  _lastRescanActiveSpaceId = null
  if (_wsHandle) {
    // wsHandle.close() handles tokenRefresher.unregister, observability dispose
    // and provider.disconnect+destroy with idempotent semantics.
    _wsHandle.close()
    _wsHandle = null
    _ws = null
  }
  if (_ydoc) {
    _ydoc.destroy()
    _ydoc = null
  }
  _currentDataspaceId = null
  _isReady = false
  // Reject pending waiters so they don't block for their full timeout.
  // Callers of waitForOrgSync / waitForEntitySystemReady handle rejection gracefully
  // via .catch(). Using reject (not resolve) prevents callers from incorrectly
  // assuming sync succeeded and proceeding with stale empty state.
  const pendingCount = _readyResolvers.length
  if (pendingCount > 0) {
    console.warn(`[OrgYDoc] Destroyed with ${pendingCount} pending waiters — rejecting them now`)
    const waiters = _readyResolvers.splice(0)
    waiters.forEach(({ reject }) => reject(new Error('[OrgYDoc] Destroyed before sync completed')))
  } else {
    _readyResolvers = []
  }
  _entityListeners.clear()
  if (_pendingFetchTimer !== null) {
    clearTimeout(_pendingFetchTimer)
    _pendingFetchTimer = null
  }
  _pendingFetchIds.splice(0)
  _pendingFetchScheduled = false
  // Clear the micro-batch edge flush state so stale edges from the old dataspace
  // are not applied to the next dataspace's tree store.
  _pendingEdgeAdds.clear()
  _pendingEdgeRemoves.clear()
  _edgeFlushScheduled = false
  // Invalidate the stable nodes snapshot so stale metadata is not served after re-init.
  _lastStableNodesVersion = -1
  _lastStableNodesSnapshot = null
  _clearRestNodesFallback()
  _notifyNodesListeners()
  _debugState = { dataspaceId: null, wsConnected: false, wsSynced: false, wsUrl: null, edgeCount: 0, nodeCount: 0, lastUpdateAt: null }
  _setConnectionStatus('disconnected')
  _setConnectionSynced(false)
  console.log('[OrgYDoc] Destroyed')
  // Perf instrumentation — track mid-load destroy events
  import('../utils/perfInstrumentation').then(({ perf }) => {
    perf.increment('orgYDocDestroyCount')
    perf.mark('org_ydoc_destroyed')
  }).catch(() => {})
}

export function waitForOrgSync(timeoutMs = 10000): Promise<void> {
  if (_isReady) {
    console.log(`[OrgYDoc] waitForOrgSync: already ready (fast path)`)
    return Promise.resolve()
  }
  console.log(`[OrgYDoc] waitForOrgSync: waiting (timeout=${timeoutMs}ms, pending=${_readyResolvers.length})`)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`[OrgYDoc] WS sync timeout (${timeoutMs}ms)`))
    }, timeoutMs)
    _readyResolvers.push({
      resolve: () => { clearTimeout(timer); console.log('[OrgYDoc] waitForOrgSync: resolved'); resolve() },
      reject: (err) => { clearTimeout(timer); reject(err) },
    })
  })
}

/** Returns true when the org Y.Doc has completed its initial WS sync. */
export function isOrgSynced(): boolean {
  return _isReady
}

/** Returns the generated pendingCmds key, e.g. so a caller can look up its eventual ack via peekCmdAck. Empty string if the org room isn't connected. */
export function writePendingCmdToOrgYDoc(
  name: string,
  params: Record<string, unknown> = {},
  opts: { targetUserId?: string; targetSpaceId?: string } = {},
): string {
  if (!_ydoc) return ''
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const data: Record<string, unknown> = { name, params, ts: Date.now() }
  if (opts.targetUserId) data.targetUserId = opts.targetUserId
  if (opts.targetSpaceId) data.targetSpaceId = opts.targetSpaceId
  _ydoc.transact(() => {
    pendingCmdsMap.set(id, JSON.stringify(data))
  })
  console.log(`[OrgYDoc] writePendingCmd: ${name}`)
  return id
}

/** Read back a cmdAck written by writeCmdAck for `id`, if still present (see CMD_ACK_TTL_MS). Null once evicted, never written, or the org room isn't connected. */
export function peekCmdAck(id: string): { ok: boolean; message?: string; error?: string; data?: unknown; ts: number } | null {
  if (!_ydoc) return null
  const raw = _ydoc.getMap<string>('cmdAcks').get(id)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}


export function writeNodeToOrgYDoc(entityId: string, data: {
  name: string
  icon: string | null
  entityType: string
  hidden?: boolean
}): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  _ydoc.transact(() => {
    nodesMap.set(entityId, JSON.stringify(data))
  })
}

/**
 * Delete a node from the local org Y.Doc, creating a Y.js tombstone.
 *
 * The tombstone propagates to the WS server on the next sync and from there
 * to all other connected clients, clearing any stale "running" Job entries that
 * survived reconnects because no delete was broadcast when the job completed.
 *
 * The `nodesMap.has()` guard is intentional: Y.js only creates a meaningful
 * tombstone when the key was previously set in THIS Y.Doc instance. Calling
 * delete on a key that was never set is a silent no-op with no propagation.
 */
export function deleteNodeFromOrgYDoc(entityId: string): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  if (!nodesMap.has(entityId)) return
  _ydoc.transact(() => {
    nodesMap.delete(entityId)
  })
}

export function subscribeToEntitySystemChanges(callback: EntityChangeCallback): () => void {
  _entityListeners.add(callback)
  return () => { _entityListeners.delete(callback) }
}

// ── Org nodes reactivity ─────────────────────────────────────────────────────
// A simple version counter lets useSyncExternalStore consumers re-render
// whenever the nodes map changes, without copying the entire Map on each tick.

/**
 * Lightweight metadata for every entity in the org Y.Doc nodes map.
 * Sufficient to render a file-tree row without loading the full entity body.
 */
export interface OrgNodeMeta {
  entityId: string
  name: string
  icon?: string
  entityType: string
  /**
   * True when the underlying entity's `hidden`/`skeleton.hidden` flag is set
   * (e.g. the per-space system "tmp" Group). Lets consumers that build a
   * lightweight stub row from this metadata alone — before the full entity
   * body ever hydrates, the common case for cross-space views like the
   * desktop "All Files" panel and Drive's flattened org root — exclude it
   * without waiting on hydration.
   */
  hidden?: boolean
}

let _nodesVersion = 0
let _lastStableNodesVersion = -1
let _lastStableNodesSnapshot: Map<string, OrgNodeMeta> | null = null
const _nodesVersionListeners = new Set<() => void>()

// ── REST fallback for node metadata ─────────────────────────────────────────
// The org Y.Doc `nodes` map is the live source, but it's WS-only — unlike
// edges (which dataspaceTreeStore falls back to REST for), there was
// previously no fallback if the WS room was slow, disconnected, or
// circuit-broken. That silently starved every consumer of getOrgNodesSnapshot
// (Drive's global "flattened org" view, SpaceSidebarV2, transparentSpaceTree)
// down to whatever the current desktop's cache already had — the failure
// mode that made the Drive root view collapse to 1-2 items for large orgs.
//
// dataspaceTreeStore.initialize() fetches
// GET /api/dataspaces/{id}/edges?include_nodes=true in parallel with the
// Y.Doc wait and calls setRestNodesFallback() with the result regardless of
// which source won the edges race, so this fallback populates independently
// of Y.Doc health.
let _restNodesFallback: Map<string, OrgNodeMeta> | null = null
let _restNodesFallbackDataspaceId: string | null = null

/**
 * Populate the REST fallback nodes map for `dataspaceId`. Ignored if it no
 * longer matches the currently active dataspace (stale response from a
 * dataspace switch that happened mid-flight).
 */
export function setRestNodesFallback(
  dataspaceId: string,
  nodes: Record<string, { name: string; icon?: string | null; entityType: string; hidden?: boolean }>,
): void {
  if (dataspaceId !== _currentDataspaceId) {
    console.log(`[OrgYDoc] Ignoring stale REST nodes fallback for ...${dataspaceId.slice(-6)} (current is ...${(_currentDataspaceId ?? '').slice(-6)})`)
    return
  }
  const map = new Map<string, OrgNodeMeta>()
  for (const [entityId, meta] of Object.entries(nodes)) {
    map.set(entityId, {
      entityId,
      name: meta.name || '',
      icon: meta.icon ?? undefined,
      entityType: meta.entityType || '',
      hidden: meta.hidden === true,
    })
  }
  _restNodesFallback = map
  _restNodesFallbackDataspaceId = dataspaceId

  // Seed-regression detector: if the WS room reports itself synced but its
  // live nodes map is empty while REST just returned real data, the org room
  // seed is broken server-side (see websocket-server seedOrgRoom) — surface
  // it loudly rather than silently limping along on the REST fallback forever.
  if (_isReady && _ydoc && map.size > 0 && _ydoc.getMap<string>('nodes').size === 0) {
    console.warn(`[OrgYDoc] ⚠️ Org Y.Doc reports synced with 0 nodes while REST fallback has ${map.size} — org room seed may be broken for ...${dataspaceId.slice(-6)}`)
  }

  console.log(`[OrgYDoc] REST nodes fallback set: ${map.size} nodes for ...${dataspaceId.slice(-6)}`)
  _notifyNodesListeners()
}

function _clearRestNodesFallback(): void {
  _restNodesFallback = null
  _restNodesFallbackDataspaceId = null
}

/**
 * Force a one-off REST refetch of node metadata for `dataspaceId`, bypassing
 * whatever the Y.Doc currently has. Used by consumers (e.g. Drive's global
 * flattened view) as a self-heal when they detect edges exist but node
 * metadata is unexpectedly empty — a state the Y.Doc's own reconnect/sync
 * logic doesn't otherwise correct on its own within a reasonable time.
 */
export async function refreshOrgNodesFallback(dataspaceId: string): Promise<void> {
  try {
    const response: any = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges?include_nodes=true`, { credentials: 'include' })
    if (response && response.nodes) {
      setRestNodesFallback(dataspaceId, response.nodes)
    } else {
      console.warn('[OrgYDoc] refreshOrgNodesFallback: unexpected response shape', response)
    }
  } catch (err) {
    console.warn('[OrgYDoc] refreshOrgNodesFallback failed:', err)
  }
}

function _notifyNodesListeners(): void {
  _nodesVersion++
  _nodesVersionListeners.forEach(fn => fn())
}

/** Subscribe to org nodes map changes. Returns an unsubscribe function. */
export function subscribeToOrgNodes(cb: () => void): () => void {
  _nodesVersionListeners.add(cb)
  return () => _nodesVersionListeners.delete(cb)
}

/** Read a stable snapshot of the org nodes map. Memoized between version ticks. */
export function getStableOrgNodes(): Map<string, OrgNodeMeta> {
  if (_nodesVersion === _lastStableNodesVersion && _lastStableNodesSnapshot !== null) {
    return _lastStableNodesSnapshot
  }
  _lastStableNodesSnapshot = getOrgNodesSnapshot()
  _lastStableNodesVersion = _nodesVersion
  return _lastStableNodesSnapshot
}

function _mergeNodesMapInto(result: Map<string, OrgNodeMeta>, nodesMap: Y.Map<string>): void {
  nodesMap.forEach((raw, entityId) => {
    try {
      const data = JSON.parse(raw)
      result.set(entityId, {
        entityId,
        name: data.name || '',
        icon: data.icon,
        entityType: data.entityType || '',
        hidden: data.hidden === true,
      })
    } catch { /* skip malformed */ }
  })
}

/**
 * Read all nodes for the current dataspace as a snapshot Map.
 * Each entry contains { entityId, name, icon?, entityType }.
 *
 * Merges, in increasing precedence: the REST fallback (if any, for the same
 * dataspace), then the shared org: room — the live Y.Doc source wins over
 * the REST fallback beneath it.
 */
export function getOrgNodesSnapshot(): Map<string, OrgNodeMeta> {
  const result = new Map<string, OrgNodeMeta>()
  if (_restNodesFallback && _restNodesFallbackDataspaceId === _currentDataspaceId) {
    _restNodesFallback.forEach((meta, entityId) => result.set(entityId, meta))
  }
  if (_ydoc) {
    _mergeNodesMapInto(result, _ydoc.getMap<string>('nodes'))
  }
  return result
}

function _mergeEdgesMapInto(result: ReturnType<typeof getOrgEdgesSnapshot>, edgesMap: Y.Map<string>): void {
  edgesMap.forEach((raw, edgeKey) => {
    try {
      const data = JSON.parse(raw)
      result.push({
        edgeId: edgeKey,  // semantic key — same format as nodesById keys
        parentId: data.parentId ?? null,
        childId: data.childId,
        pos: data.pos || 'M',
        expanded: data.expanded ?? false,
        kind: data.kind ?? 'canonical',
      })
    } catch { /* skip malformed */ }
  })
}

/**
 * Read all edges from the org: room as a single snapshot. Each entry's
 * `edgeId` is the semantic key "(parentId or 'ROOT'):childId" which equals
 * getEdgeKey(childId, parentId) in dataspaceTreeStore.
 */
export function getOrgEdgesSnapshot(): Array<{
  edgeId: string
  parentId: string | null
  childId: string
  pos: string
  expanded: boolean
  kind: 'canonical' | 'alias'
}> {
  const result: ReturnType<typeof getOrgEdgesSnapshot> = []
  if (_ydoc) {
    _mergeEdgesMapInto(result, _ydoc.getMap<string>('edges'))
  }
  return result
}

function _applyFullTreeSync(edgesMap: Y.Map<string>): Promise<void> {
  return import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
    const store = useDataspaceTreeStore.getState()

    // If the tree hasn't been initialized yet, do a full replace from the Y.Doc.
    // If already initialized, merge — add/update edges without removing REST-sourced ones.
    if (!store.initialized) {
      const edges: Array<{
        id: string; dataspaceId: string; parentId: string | null; childId: string;
        pos: string; expanded: boolean; kind: 'canonical' | 'alias'; createdAt: string; updatedAt: string;
      }> = []

      edgesMap.forEach((raw, edgeKey) => {
        // edgeKey is the semantic key "(parentId or 'ROOT'):childId" after Phase 0a
        try {
          const data = JSON.parse(raw)
          edges.push({
            id: edgeKey,  // semantic key — used as nodesById key
            dataspaceId: _currentDataspaceId || '',
            parentId: data.parentId ?? null,
            childId: data.childId,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
            createdAt: '',
            updatedAt: '',
          })
        } catch { /* skip malformed */ }
      })
      console.log(`[OrgYDoc] Full tree sync (init): ${edges.length} edges → initFromEdges`)
      store.initFromEdges(edges)
      return
    }

    // Merge: add or update edges from Y.Doc; don't remove edges not in Y.Doc.
    // edgeKey IS the nodesById key — no conversion needed.
    let merged = 0
    edgesMap.forEach((raw, edgeKey) => {
      try {
        const data = JSON.parse(raw)
        const existing = store.nodesById[edgeKey]
        if (!existing || existing.pos !== (data.pos || 'M') || existing.expanded !== (data.expanded ?? false)) {
          store._applyAddNode(edgeKey, {
            id: data.childId,
            parentId: data.parentId ?? null,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
          })
          merged++
        }
      } catch { /* skip malformed */ }
    })
    if (merged > 0) {
      store._rebuildChildrenMap()
      console.log(`[OrgYDoc] Full tree sync (merge): ${merged} edges merged into existing tree`)
    }
  }).catch(err => {
    console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
  })
}

function _applyFullNodeSync(nodesMap: Y.Map<string>): Promise<void> {
  // Walk the full nodes map and reconcile with whatever the registry already
  // holds. Only mutates entities the owning store already knows about — full
  // hydration of unknown entities flows through the per-space loader / global
  // store / app stores, not through this metadata sync.
  let updated = 0
  nodesMap.forEach((raw, entityId) => {
    try {
      const data = JSON.parse(raw)
      const existing = entityRegistry.getById(entityId)
      if (!existing) return
      const incomingName = data.name || existing.name
      const nameChanged = data.name && existing.name !== data.name
      const iconChanged = (existing as any).icon !== data.icon
      const existingSkeleton = (existing as any).skeleton
      const skeletonChanged = data.skeleton !== undefined && existingSkeleton !== undefined && (
        JSON.stringify(existingSkeleton) !== JSON.stringify({ ...existingSkeleton, ...data.skeleton })
      )
      if (nameChanged || iconChanged || skeletonChanged) {
        const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
        if (data.skeleton !== undefined) {
          merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
        }
        if (data.updated_at) merged.updated_at = data.updated_at
        entityRegistry.upsert(merged)
        updated++
      }
    } catch { /* skip */ }
  })
  if (updated > 0) {
    console.log(`[OrgYDoc] Full node sync: updated ${updated} entity names/skeletons`)
  }
  _notifyNodesListeners()
  return Promise.resolve()
}

// Pending edge changes accumulated across multiple Yjs observer callbacks in the same tick.
// Flushed once per macrotask via setTimeout(0) so back-to-back Yjs messages only trigger
// a single _applyEdgeBatch (and therefore a single _rebuildChildrenMap).
type EdgeNode = { id: string; parentId: string | null; pos: string; expanded: boolean; kind: 'canonical' | 'alias' }
const _pendingEdgeAdds = new Map<string, EdgeNode>()
const _pendingEdgeRemoves = new Set<string>()
let _edgeFlushScheduled = false

function _scheduleEdgeFlush(): void {
  if (_edgeFlushScheduled) return
  _edgeFlushScheduled = true
  setTimeout(() => {
    _edgeFlushScheduled = false
    if (_pendingEdgeAdds.size === 0 && _pendingEdgeRemoves.size === 0) return
    const adds = Array.from(_pendingEdgeAdds.entries()) as [string, EdgeNode][]
    const removes = Array.from(_pendingEdgeRemoves)
    _pendingEdgeAdds.clear()
    _pendingEdgeRemoves.clear()
    import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
      useDataspaceTreeStore.getState()._applyEdgeBatch(adds, removes)
    }).catch(err => {
      console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
    })
  }, 0)
}

function _handleEdgeChanges(event: Y.YMapEvent<string>, edgesMap: Y.Map<string>): void {
  // Capture changes synchronously — event.changes is invalidated once the observer returns.
  for (const [edgeKey, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      const raw = edgesMap.get(edgeKey)
      if (!raw) continue
      try {
        const data = JSON.parse(raw)
        const parentId = data.parentId ?? null
        if (parentId !== null && parentId === data.childId) {
          console.error('[OrgYDoc] _handleEdgeChanges: dropping self-parent edge', edgeKey)
          continue
        }
        _pendingEdgeAdds.set(edgeKey, {
          id: data.childId,
          parentId,
          pos: data.pos || 'M',
          expanded: data.expanded ?? false,
          kind: data.kind ?? 'canonical',
        })
        _pendingEdgeRemoves.delete(edgeKey) // add wins over remove if both arrive
      } catch { /* skip malformed */ }
    } else if (change.action === 'delete') {
      _pendingEdgeRemoves.add(edgeKey)
      _pendingEdgeAdds.delete(edgeKey)
    }
  }

  _scheduleEdgeFlush()
}

function _emitEntityEvent(type: 'created' | 'updated' | 'deleted', entity: any): void {
  import('../services/entityEventService').then(({ entityEventService }) => {
    // Use emitEntityEvent directly so we can set source: 'crdt'. Editors
    // (NotesEditor, PageEditor, etc.) and requestCacheService need to see
    // these CRDT-originating changes; per-action subscribers ignore 'local'
    // events because they were already processed by the originating action.
    entityEventService.emitEntityEvent({
      type,
      entity,
      spaceId: entity.spaceId || _currentDataspaceId || '',
      source: 'crdt',
    })
  }).catch(() => {})
}

// Pending entity IDs that arrived via CRDT 'add' but aren't in the entity store.
// Collected within the current setTimeout window and flushed as a single batch request.
const _pendingFetchIds: string[] = []
let _pendingFetchScheduled = false
let _pendingFetchTimer: ReturnType<typeof setTimeout> | null = null

const BATCH_FETCH_LIMIT = 200

async function _executeBatchFetch(ids: string[], scheduledForDataspaceId: string | null): Promise<void> {
  // Split into chunks of BATCH_FETCH_LIMIT to stay within URL length limits.
  // Hydrated entities are dispatched through entityRegistry.upsert which routes
  // each one to its owning store (space/global/app) by entityType.
  //
  // Cross-org contamination guard: if the active dataspace changes while this
  // batch is mid-flight (org switch), drop the result on the floor. Otherwise
  // the registry would dispatch entities from the old org into the new org's
  // global/space caches, which both makes the UI lie and corrupts IDB.
  for (let i = 0; i < ids.length; i += BATCH_FETCH_LIMIT) {
    const chunk = ids.slice(i, i + BATCH_FETCH_LIMIT)
    const idsParam = chunk.map(encodeURIComponent).join(',')
    try {
      const result: any = await fetchService(`${API_BASE_URL}/entities?ids=${idsParam}`, { credentials: 'include' })
      if (_currentDataspaceId !== scheduledForDataspaceId) {
        console.log(`[OrgYDoc] Batch fetch result discarded — dataspace switched from ...${(scheduledForDataspaceId || '').slice(-6)} to ...${(_currentDataspaceId || '').slice(-6)}`)
        return
      }
      const entities: any[] = Array.isArray(result) ? result : (result?.entities ?? [])
      for (const entity of entities) {
        if (!entity?._id) continue
        // Mail entities are owned by mailAppStore — route via the dedicated bridge.
        if (entity.entityType && MAIL_CRDT_TYPES.has(entity.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(entity)).catch(() => {})
          _emitEntityEvent('created', entity)
          continue
        }
        entityRegistry.upsert(entity)
        _emitEntityEvent('created', entity)
      }
      console.log(`[OrgYDoc] Batch fetch complete: ${entities.length}/${chunk.length} entities dispatched via registry`)
    } catch (err) {
      console.warn(`[OrgYDoc] Batch fetch failed for ${chunk.length} entities:`, err)
    }
  }
}

function _scheduleBatchFetch(): void {
  if (_pendingFetchScheduled) return
  _pendingFetchScheduled = true
  // Use setTimeout instead of queueMicrotask: each WebSocket message arrives as a
  // separate event loop task, so queueMicrotask only collects IDs within a single
  // message. A 50ms window collapses all entity-add events from a burst of WS
  // messages into one batch request. The timer ID is stored so destroyOrgYDocStore
  // can cancel it and prevent stale cross-session fetches.
  _pendingFetchTimer = setTimeout(() => {
    _pendingFetchTimer = null
    const ids = _pendingFetchIds.splice(0)
    _pendingFetchScheduled = false
    if (ids.length === 0) return

    // Capture the dataspace at scheduling time so the post-await dispatch
    // can bail if the user switched orgs while the request was in flight.
    const scheduledFor = _currentDataspaceId
    log.debug(`[OrgYDoc] Batch-fetching ${ids.length} new entities from REST (dataspace=...${(scheduledFor || '').slice(-6)})`)
    _executeBatchFetch(ids, scheduledFor).catch(() => {})
  }, 50)
}

function _handleNodeChanges(event: Y.YMapEvent<string>, nodesMap: Y.Map<string>): void {
  // Capture changes synchronously while still inside the YJS observer callback.
  // event.changes is invalidated once the observer returns, so accessing it
  // inside an async .then() throws "You must not compute changes after the
  // event-handler fired."
  //
  // `nodesMap` is passed explicitly (not read from the module-level `_ydoc`)
  // rather than captured, keeping this function reusable across callers.
  type NodeChange = { entityId: string; action: 'add' | 'update' | 'delete'; raw: string | null }
  const captured: NodeChange[] = []

  for (const [entityId, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      captured.push({ entityId, action: change.action, raw: nodesMap?.get(entityId) ?? null })
    } else if (change.action === 'delete') {
      captured.push({ entityId, action: 'delete', raw: null })
    }
  }

  if (captured.length === 0) return

  for (const { entityId, action, raw } of captured) {
    if (action === 'add' || action === 'update') {
      if (!raw) continue
      try {
        const data = JSON.parse(raw)

        // Mail entities are not in the global entity store — route via the bridge.
        if (data.entityType && MAIL_CRDT_TYPES.has(data.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(data)).catch(() => {})
          _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
          continue
        }

        const existing = entityRegistry.getById(entityId)
        if (existing) {
          // Skeleton-only broadcasts (e.g. entity_update writing inboxProcessing)
          // send name:"" which must not overwrite the real entity name. Only count
          // a name change when the incoming name is non-empty.
          const incomingName = data.name || existing.name
          const nameChanged = data.name && existing.name !== data.name
          const iconChanged = (existing as any).icon !== data.icon
          const skeletonChanged = data.skeleton !== undefined && (
            JSON.stringify((existing as any).skeleton) !== JSON.stringify({ ...((existing as any).skeleton ?? {}), ...data.skeleton })
          )
          if (nameChanged || iconChanged || skeletonChanged) {
            const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
            if (data.skeleton !== undefined) {
              merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
            }
            // Propagate updated_at from the broadcast so downstream consumers
            // (automationsStore fingerprint, AutomationForm) see the correct
            // timestamp. Cold-load seeds don't include updated_at; only overwrite
            // when the broadcast explicitly carries it.
            if (data.updated_at) merged.updated_at = data.updated_at
            entityRegistry.upsert(merged)
            _emitEntityEvent('updated', merged)
          }
        } else if (action === 'add') {
          // First time we're seeing this entity — enqueue a batch fetch so we
          // hydrate the full record via REST and dispatch through the registry.
          // We restrict this to `add` events: an `update` for an entity not in
          // any local store usually means the owning store hasn't mounted yet
          // (e.g. opening a space that hasn't been visited this session). The
          // store will hydrate on its own; fetching here would just duplicate
          // that work and burn a network round-trip.
          _pendingFetchIds.push(entityId)
          _scheduleBatchFetch()
        }
        _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
      } catch { /* skip malformed entry */ }
    } else if (action === 'delete') {
      // Mail bridge handles its own types; for everything else dispatch the
      // tombstone through the registry. The deleted node's type is no longer
      // readable from the Y.Map, so we let the registry fan out to all scopes.
      import('./mailCrdtBridge').then(({ handleMailCrdtDelete }) => handleMailCrdtDelete(entityId)).catch(() => {})
      const deletedEntity = entityRegistry.getById(entityId)
      entityRegistry.deleteEntity(entityId)
      if (deletedEntity) _emitEntityEvent('deleted', deletedEntity)
      _entityListeners.forEach(fn => fn('delete', entityId))
    }
  }
  _notifyNodesListeners()
}
